/* eslint-disable react/no-multi-comp */
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import classNames from "classnames/bind";
import { graphql } from "gatsby";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import React from "react";

import * as imageStyles from "../styles/image.module.scss";
import * as linkStyles from "../styles/link.module.scss";
import * as videoStyles from "../styles/video.module.scss";

import InnerHTMLWithScript from "./InnerHTMLWithScript";
import Link from "./Link";
import MenuBar from "./MenuBar";
import Tooltip from "./Tooltip";
import Video from "./Video";
import ClassUtils from "../utils/ClassUtils";
import ContentUtils from "../utils/ContentUtils";
import LinkUtils from "../utils/LinkUtils";

const handleEmbeddedLinkBlock = (node) => {
	const { asset, page, linkText } = node.data.target;
	let url;
	if (page) {
		url = page;
	} else if (asset) {
		url = asset.file ? asset.file.url : "";
	}

	return (
		<div className={linkStyles.buttonContainer}>
			<Link to={url} className={linkStyles.button}>
				{linkText}
			</Link>
		</div>
	);
};
const handleInlineHyperLink = (node) => {
	const { asset, page, linkText, url } = node.data.target;

	let goToUrl;
	let isExternal = false;
	if (url) {
		goToUrl = url;
		isExternal = true;
	} else if (page) {
		goToUrl = page;
	} else if (asset) {
		url = asset.file ? asset.file.url : "";
	}

	return (
		<Link
			to={goToUrl}
			{...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
		>
			{linkText}
		</Link>
	);
};
const handleEmbeddedImageBlock = (node) => {
	const { file, title, position, clear } = node.data.target;
	const url = file && file.file ? file.file.url : "";

	const imageStates = classNames.bind(imageStyles);
	const imageClass = imageStates(imageStyles.image, {
		left: position === "left",
		right: position === "right",
		clearLeft: clear === "left",
		clearRight: clear === "right",
		clearBoth: clear === "both",
	});
	return <img src={url} className={imageClass} alt={title} />;
};
const handleEmbeddedVideoBlock = (node) => {
	const { url } = node.data.target;

	const youtubeId = LinkUtils.getYouTubeIdFromLink(url);
	return (
		<div className={videoStyles.video}>
			<Video url={youtubeId} />
		</div>
	);
};
const handleEmbeddedHtmlBlock = (node) => {
	const html = node.data?.target?.code?.code;
	return <InnerHTMLWithScript html={html} />;
};
const handleColoredTextBlock = (node) => {
	const fields = node.data.target.fields;
	const color = fields.color ? fields.color[ContentUtils.DEFAULT_CULTURE] : "";
	const content = fields.content
		? fields.content[ContentUtils.DEFAULT_CULTURE]
		: "";

	return (
		<div className={ClassUtils.getRichTextColorClassName(color)}>
			{renderRichText(content)}
		</div>
	);
};
const handleEmbeddedAnchorBlock = (node) => {
	const { anchorId } = node.data.target;
	if (!anchorId) return <div></div>;
	return (
		<div
			id={anchorId}
			style={{ position: "relative", height: 0, overflow: "hidden" }}
		/>
	);
};
const handleEmbeddedMenuBarBlock = (node) => {
	const { menuItems = [], sticky, centerContent } = node.data.target;
	const items = (menuItems || []).map((item) => ({
		id: item.contentful_id,
		title: item.title,
		icon: item.icon?.file?.url ? (
			<img src={item.icon.file.url} alt={item.title} />
		) : null,
		cta: item.cta,
		description: item.description,
		linkId: item.link,
	}));
	return (
		<MenuBar
			items={items}
			sticky={sticky !== false}
			center={centerContent === true}
		/>
	);
};
const handleEmbeddedEntryBlock = (node) => {
	const entryType = ContentUtils.getEntryBlockType(node);
	switch (entryType) {
		case ContentUtils.CONTENT_LINK:
			return handleEmbeddedLinkBlock(node);
		case ContentUtils.CONTENT_VIDEO:
			return handleEmbeddedVideoBlock(node);
		case ContentUtils.CONTENT_IMAGE:
			return handleEmbeddedImageBlock(node);
		case ContentUtils.CONTENT_EMBEDDED_HTML:
			return handleEmbeddedHtmlBlock(node);
		case ContentUtils.CONTENT_COLORED_TEXT:
			return handleColoredTextBlock(node);
		case ContentUtils.CONTENT_MENU_BAR:
			return handleEmbeddedMenuBarBlock(node);
		case ContentUtils.CONTENT_ANCHOR:
			return handleEmbeddedAnchorBlock(node);
	}
	return <div></div>;
};
const handleAssetHyperLink = (node) => {
	const data = node.data;
	const asset = data.target && data.target.file ? data.target.file.url : "";
	return asset ? (
		<Link to={asset}>
			{renderRichText({ raw: JSON.stringify(node.content[0]) })}
		</Link>
	) : (
		<span></span>
	);
};
const handleHyperLink = (node) => {
	const uri = node.data ? node.data.uri : null;

	if (!uri) return <span></span>;

	// Check if the link is external (starts with http:// or https://)
	// Links starting with "/" are considered internal
	const isExternal = /^https?:\/\//.test(uri) && !uri.startsWith("/");

	// Render link with appropriate target for external/internal links
	return (
		<Link
			to={uri}
			{...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
		>
			{renderRichText({ raw: JSON.stringify(node.content[0]) })}
		</Link>
	);
};
const handleEmbeddedInlineGlossaryItem = (node) => {
	const target = node.data.target;
	if (!target) return <div></div>;
	const title = target.title;
	let description = target.tooltip.tooltip;
	return (
		<Tooltip
			title={title}
			description={description}
			url={LinkUtils.getGlossaryItemLink(title)}
		/>
	);
};
const RichText = (props) => {
	const { description } = props;
	const Bold = ({ children }) => <strong>{children}</strong>;
	const Text = ({ children }) => <p>{children}</p>;

	const options = {
		renderMark: {
			[MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: (text) => <i>{text}</i>,
		},
		renderNode: {
			[BLOCKS.PARAGRAPH]: (_node, children) => {
				return <Text>{children}</Text>;
			},
			[BLOCKS.EMBEDDED_ENTRY]: (node) => {
				return handleEmbeddedEntryBlock(node);
			},
			[ContentUtils.EMBEDDED_INLINE]: (node) => {
				const entryType = ContentUtils.getEntryBlockType(node);
				if (entryType === ContentUtils.CONTENT_GLOSSARY_ITEM) {
					return handleEmbeddedInlineGlossaryItem(node);
				}
				if (entryType === ContentUtils.CONTENT_LINK) {
					return handleInlineHyperLink(node);
				}
				if (entryType === ContentUtils.CONTENT_EXTERNAL_LINK) {
					return handleInlineHyperLink(node);
				}
				return <></>;
			},
			[ContentUtils.ASSET_HYPERLINK]: (node) => {
				return handleAssetHyperLink(node);
			},
			[ContentUtils.HYPERLINK]: (node) => {
				return handleHyperLink(node);
			},
		},
		renderText: (text) => {
			return text.split("\n").reduce((children, textSegment, index) => {
				return [...children, index > 0 && <br key={index} />, textSegment];
			}, []);
		},
	};
	if (description == null) {
		return <></>;
	}
	return <div>{renderRichText(description, options)}</div>;
};

export const query = graphql`
	fragment richTextReferences on Node {
		__typename
		... on ContentfulGlossaryItem {
			contentful_id

			title
			tooltip {
				tooltip
			}
		}
		... on ContentfulImage {
			contentful_id
			file {
				file {
					url
				}
			}
			title
			position
			clear
		}
		... on ContentfulLink {
			contentful_id
			linkText
			page
			asset {
				file {
					url
				}
			}
		}
		... on ContentfulExternalLink {
			contentful_id
			linkText
			url
			asset {
				file {
					url
				}
			}
		}
		... on ContentfulVideo {
			contentful_id
			url
		}
		... on ContentfulEmbeddedHtml {
			contentful_id
			code {
				code
			}
		}
		... on ContentfulAsset {
			contentful_id
			file {
				url
			}
		}
		... on ContentfulMenuBar {
			contentful_id
			sticky
			centerContent
			menuItems {
				contentful_id
				title
				icon {
					file {
						url
					}
				}
				cta
				description {
					raw
				}
				link
			}
		}
		... on ContentfulAnchor {
			contentful_id
			anchorId
		}
	}
`;

export default RichText;
